import type { AlertType } from './Alert';
import type GarminCourse from './GarminCourse';
import type { MapPhoto, Splits } from './Map';
import type TrailInfo from './TrailInfo';
import type TrailRating from './TrailRating';
import type { User } from './User';
import type { Visibility } from './Visibility';
import type { WaypointCategory } from './Waypoint';
import type Waypoint from './Waypoint';

export type EstimatedTimeToComplete = {
  value: string;
  unit: string;
};

export type Location = {
  postalCode?: string;
  city?: string;
  region?: string;
  country?: string;
  countryId?: number;
  latitude?: string;
  longitude?: string;
};

// Note that this type only defines the fields needed for custom routes; it does not define all fields returned by the API
export type CustomRouteMap = {
  id: number;
  bounds?: {
    longitudeTopLeft: string | number;
    latitudeTopLeft: string | number;
    longitudeBottomRight: string | number;
    latitudeBottomRight: string | number;
  };
  name?: string;
  description?: string;
  /** Original-language / source description when translated; prefer for display over `description`. */
  description_source?: string;
  contentPrivacy?: Visibility;
  slug: string;
  /** Numeric trail id when this map/recording is linked to a trail (`trailId` in API JSON). */
  trailId?: number;
  activity: { uid: string };
  summaryStats: {
    distanceTotal: number;
    elevationGain: number;
    duration: number;
    // Populated when the map is a recording (presentationType: 'track').
    // speedAverage / paceAverage / calories require both timeMoving and distanceTotal in the source data.
    calories?: number;
    elevationLoss?: number;
    timeMoving?: number;
    timeTotal?: number;
    speedAverage?: number;
  };
  estimatedTimeToComplete?: EstimatedTimeToComplete;
  routes: {
    description?: string;
    estimatedTimeToComplete?: EstimatedTimeToComplete;
    id: number;
    isLinkedToNextSegment?: boolean;
    lineDisplayProperty?: { color: string };
    lineGeoStats: { timeTotal?: number; distanceTotal: number; elevationGain: number };
    lineSegments: { sequence_num: number; polyline: { pointsData: string }; freeDraw?: boolean }[];
    name?: string;
    sequence_num: number;
  }[];
  // Recordings (presentationType: 'track') return their polyline data under `tracks`
  // instead of `routes`. The fields carry the same info but under renamed keys:
  // `lineSegments` -> `lineTimedSegments`, `lineGeoStats` -> `lineTimedGeoStats`.
  tracks?: {
    description?: string;
    estimatedTimeToComplete?: EstimatedTimeToComplete;
    id: number;
    lineDisplayProperty?: { color: string };
    lineTimedGeoStats: { timeTotal?: number; distanceTotal: number; elevationGain: number };
    lineTimedSegments: { sequence_num: number; polyline: { pointsData: string }; freeDraw?: boolean }[];
    name?: string;
    sequence_num: number;
  }[];
  waypoints: Waypoint[];
  mapPhotos?: MapPhoto[];
  metadata: { created: string; updated: string };
  garminCourse?: GarminCourse;
  // Server-computed; only populated for recordings (presentationType: 'track').
  splits?: Splits;
  // Pre-formatted source label like "Garmin Fenix". Only set for third-party imports.
  attribution?: string;
  location: Location;
  user: User;
  // Review fields populated by the API when the recording has an associated trail review.
  // Mirrors the shape emitted by `at_maps_shallow_presenter` in the Rails app.
  rating?: number;
  comment?: string;
  difficulty?: number | null;
  associatedTrailReviewId?: string;
  ratingAttributes?: { uid: TrailRating; name: string }[];
  infoAttributes?: { uid: TrailInfo; name: string }[];
};

export type CustomRouteSegmentIndex = number;

// Note that this ID is for the sake of ordering and does not correspond to the ID returned from the API
// We define this type since custom routes do not use all the fields returned by the API
export type CustomRouteWaypointId = number;
export type CustomRouteWaypoint = {
  id: CustomRouteWaypointId;
  name: string;
  description?: string;
  showTitle?: boolean;
  latitude: number;
  longitude: number;
  serverId?: number;
  isTemporary?: boolean;
  waypointCategory?: WaypointCategory;
  contentPrivacy?: string;
  isGlobal?: boolean;
};

export enum ActivityType {
  Hiking = 'hiking',
  MountainBiking = 'mountain-biking',
  RoadBiking = 'road-biking',
  OffRoadDriving = 'off-road-driving',
  ScenicDriving = 'scenic-driving'
}

export enum CustomRoutesSort {
  DateUpdated = 'date_updated',
  DateCreated = 'date_created',
  Name = 'name'
}

export enum ActivitySort {
  ActivityDate = 'activity_date',
  Name = 'name',
  LongestDistance = 'longest_distance',
  MostElevationGain = 'most_elevation_gain'
}

export type CustomRouteType = 'map';

export type UserCustomRoute = {
  id: number;
  _geoloc: {
    lat: number;
    lng: number;
  };
  activities: string[];
  activity: {
    uid: string;
    name: string;
  };
  activity_names: string[];
  avg_rating: number | null;
  city_name: string;
  contentPrivacy: Visibility;
  country_name: string;
  created_at: string;
  updated_at: string;
  distance: number;
  duration_minutes: number;
  elevation_gain: number;
  estimated_time_to_complete: {
    value: string;
    unit: string;
  };
  hidden: number;
  indexed_at: number;
  length: number;
  missing_polyline: boolean;
  name: string;
  objectID: string;
  photo_count: number;
  popularity: number;
  private: boolean;
  rating: number | null;
  slug: string;
  state_name: string;
  timezone: string | null;
  trail_id: number;
  type: CustomRouteType;
  units: string;
  user: {
    id: number;
    first_name: string;
    last_name: string;
    slug: string;
    profile_photo_url: string;
  };
};

export type MarkerProperties = {
  id: string;
  segmentIndex: number;
  isStart?: boolean;
  isEnd?: boolean;
  isLinkedToAdjacentSegment?: boolean;
  pathIndex: number;
  endPathIndex?: number;
  latitude: number;
  longitude: number;
};

export type LineProperties = {
  id: string;
  pathIndex: number;
  lineColor: string;
  lineOutlineColor: string;
  freeDraw?: boolean;
};

export type Costings = {
  leastGain: number;
  mostScenic: number;
  mostPopular: number;
  makeShorter: number;
};

export type CostingsKeys = keyof Costings;

export type CostingsConfig = {
  [costing in CostingsKeys]: {
    minimum: number;
    maximum: number;
    default: number;
  };
};

export type CustomRouteAlert = {
  iconCode: string;
  level: string;
  message: string;
  type: AlertType;
};
